import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import { FunctionComponent, useRef, useState } from "react";
import { createReport } from "../../api-clients/iwannacamp-api";
import useIdToken from "../../hooks/useIdToken";
import { createLogger } from "../../utils/logger";

const logger = createLogger("ReportIssueModal");

interface ReportIssueModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Campground SQID */
  campgroundSqid: string;
  /** Campground name for display */
  campgroundName: string;
}

const REPORT_TYPES = [
  { value: "incorrect_info", label: "Incorrect Information" },
  { value: "closed", label: "Campground is Closed" },
  { value: "unsafe", label: "Safety Concern" },
  { value: "other", label: "Other Issue" },
];

export const ReportIssueModal: FunctionComponent<ReportIssueModalProps> = ({
  opened,
  onClose,
  campgroundSqid,
  campgroundName,
}) => {
  const idToken = useIdToken();
  const [reportType, setReportType] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const handleSubmit = async () => {
    if (!reportType || !description.trim() || !turnstileToken || !idToken) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createReport({
        path: { campground_sqid: campgroundSqid },
        body: {
          report_type: reportType as
            | "incorrect_info"
            | "closed"
            | "unsafe"
            | "other",
          description: description.trim(),
          turnstile_token: turnstileToken,
        },
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      notifications.show({
        title: "Report Submitted",
        message: "Thank you for helping improve campground information!",
        color: "green",
      });

      // Reset form and close
      setReportType(null);
      setDescription("");
      setTurnstileToken("");
      turnstileRef.current?.reset();
      onClose();
    } catch (error) {
      logger.error("Failed to submit report:", error);
      notifications.show({
        title: "Error",
        message: "Failed to submit report. Please try again.",
        color: "red",
      });
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setReportType(null);
    setDescription("");
    setTurnstileToken("");
    turnstileRef.current?.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Report Issue: ${campgroundName}`}
      size="md"
      zIndex={310}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Help us improve campground information by reporting issues you've
          noticed.
        </Text>

        <Select
          label="Issue Type"
          placeholder="Select issue type"
          data={REPORT_TYPES}
          value={reportType}
          onChange={setReportType}
          required
          comboboxProps={{ withinPortal: true, zIndex: 320 }}
        />

        <Textarea
          label="Description"
          placeholder="Please provide details about the issue..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          autosize
          minRows={4}
          maxRows={10}
          maxLength={2000}
          required
        />

        {/* Cloudflare Turnstile for spam protection */}
        {siteKey && (
          <Turnstile
            ref={turnstileRef}
            siteKey={siteKey}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken("")}
            onExpire={() => setTurnstileToken("")}
          />
        )}

        <Group justify="flex-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !reportType ||
              description.trim().length < 10 ||
              !turnstileToken ||
              isSubmitting
            }
            loading={isSubmitting}
          >
            Submit Report
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ReportIssueModal;
